import React, { useState } from 'react';
import { UserPlus, Shield, User, Check, AlertTriangle, Mail, X } from 'lucide-react';
import { api } from '../../api/client';

const ROLE_OPTIONS = [
  {
    value: 'owner',
    label: 'Full Access',
    icon: Shield,
    description: 'Full access to everything you can access, including billing and app management.',
  },
  {
    value: 'sub_user',
    label: 'Limited Access',
    icon: User,
    description: 'Limited access. You can configure exactly what this user can see and do after they join.',
  },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_MAX_LENGTH = 30;

export default function InviteUsers() {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', role: 'sub_user' });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);
  const [invites, setInvites] = useState([]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const nextErrors = {};
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const email = form.email.trim();

    if (!firstName) {
      nextErrors.firstName = 'First name is required';
    } else if (firstName.length > NAME_MAX_LENGTH) {
      nextErrors.firstName = `First name must be ${NAME_MAX_LENGTH} characters or fewer`;
    }

    if (!lastName) {
      nextErrors.lastName = 'Last name is required';
    } else if (lastName.length > NAME_MAX_LENGTH) {
      nextErrors.lastName = `Last name must be ${NAME_MAX_LENGTH} characters or fewer`;
    }

    if (!email) {
      nextErrors.email = 'Email is required';
    } else if (!EMAIL_PATTERN.test(email)) {
      nextErrors.email = 'Enter a valid email address';
    } else if (invites.some((invite) => invite.email.toLowerCase() === email.toLowerCase())) {
      nextErrors.email = 'An invite has already been sent to this email';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(null);
    if (!validate()) return;

    setSending(true);
    try {
      const res = await api.post('/api/company/invite', {
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email: form.email.trim(),
        role: form.role,
      });

      setInvites((prev) => [
        {
          id: res.data.invite_id,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          role: form.role,
          status: 'pending',
        },
        ...prev,
      ]);
      setSuccess(`Invite sent to ${form.email.trim()}`);
      setForm({ firstName: '', lastName: '', email: '', role: 'sub_user' });
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to send invite:', err);
      setErrors({ email: err.response?.data?.detail || 'Failed to send invite' });
    } finally {
      setSending(false);
    }
  };

  const revokeInvite = (id) => {
    setInvites((prev) => prev.filter((invite) => invite.id !== id));
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-3">
          <UserPlus className="w-8 h-8 text-blue-600" />
          Invite Users
        </h1>
        <p className="text-gray-600 mt-1">
          Invite people to your company and choose whether they join with Full Access or Limited Access.
        </p>
      </div>

      {/* Alerts */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-center gap-3">
          <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-green-800">{success}</span>
        </div>
      )}

      {/* Invite Form */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b bg-gray-50">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            Send an Invite
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">
            They'll receive an email with a link to join your company
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">First Name</label>
              <input
                type="text"
                required
                maxLength={NAME_MAX_LENGTH}
                value={form.firstName}
                onChange={(e) => updateField('firstName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.firstName ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Jane"
              />
              {errors.firstName && <p className="text-sm text-red-600 mt-1">{errors.firstName}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Last Name</label>
              <input
                type="text"
                required
                maxLength={NAME_MAX_LENGTH}
                value={form.lastName}
                onChange={(e) => updateField('lastName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.lastName ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Doe"
              />
              {errors.lastName && <p className="text-sm text-red-600 mt-1">{errors.lastName}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.email ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="jane.doe@example.com"
            />
            {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ROLE_OPTIONS.map((option) => {
                const Icon = option.icon;
                const selected = form.role === option.value;
                return (
                  <label
                    key={option.value}
                    className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                      selected ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={option.value}
                      checked={selected}
                      onChange={(e) => updateField('role', e.target.value)}
                      className="mt-1"
                    />
                    <div>
                      <div className={`flex items-center gap-2 font-medium ${selected ? 'text-blue-700' : 'text-gray-800'}`}>
                        <Icon className="w-4 h-4" />
                        {option.label}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">{option.description}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={sending}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UserPlus className="w-4 h-4" />
              {sending ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
        </form>
      </div>

      {/* Pending Invites */}
      <div className="bg-white rounded-lg shadow-sm border mt-6">
        <div className="px-6 py-4 border-b bg-gray-50">
          <h3 className="font-semibold text-gray-800">Pending Invites</h3>
          <p className="text-sm text-gray-500 mt-0.5">Invites that haven't been accepted yet</p>
        </div>

        {invites.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-gray-300" />
            No invites sent yet
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="px-6 py-3 font-medium">Name</th>
                  <th className="px-6 py-3 font-medium">Email</th>
                  <th className="px-6 py-3 font-medium">Role</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((invite) => (
                  <tr key={invite.id} className="border-b last:border-b-0">
                    <td className="px-6 py-3 text-gray-800">
                      {invite.firstName} {invite.lastName}
                    </td>
                    <td className="px-6 py-3 text-gray-600">{invite.email}</td>
                    <td className="px-6 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          invite.role === 'owner' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {invite.role === 'owner' ? 'Full Access' : 'Limited Access'}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Pending
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <button
                        onClick={() => revokeInvite(invite.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Revoke invite"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
